import { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import {
  createEmptyTemplateDraftDefaults,
  TemplateDraftChecklistAnswer,
  TemplateDraftChecklistItem,
  TemplateDraftDefaultValues,
  TemplateDraftSection,
  toTemplateDraftDefaults,
} from 'shared/domain/template/templateWizardDraft';
import { showErrorToastWithMessage } from 'services/toaster';
import { FormChecklistAnswer, FormChecklistItem, FormSection } from '../form/types';
import { TemplateDefaultValues } from './types';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';

function toFormChecklistAnswer(
  answer: TemplateDraftChecklistAnswer
): FormChecklistAnswer {
  const base = {
    _id: answer._id,
    uniqueKeyId: answer._id || answer.uniqueKeyId,
    label: answer.label,
    isRequired: answer.isRequired,
  };

  switch (answer.type) {
    case AnswerType.Compliance:
      return {
        ...base,
        type: AnswerType.Compliance,
        optionLabels: answer.optionLabels.map((ol) => ({
          option: ol.option,
          label: ol.label,
        })),
      };
    case AnswerType.Numeric:
      return {
        ...base,
        type: AnswerType.Numeric,
        unit: answer.unit,
        normMin: answer.normMin,
        normMax: answer.normMax,
        rangeMin: answer.rangeMin,
        rangeMax: answer.rangeMax,
        step: answer.step,
      };
    case AnswerType.Text:
      return {
        ...base,
        type: AnswerType.Text,
      };
    case AnswerType.OptionSelection:
      return {
        ...base,
        type: AnswerType.OptionSelection,
        options: answer.options.map((opt) => ({
          _id: opt._id,
          label: opt.label,
        })),
        isMultiselect: answer.isMultiselect,
      };
  }
}

function toFormChecklistItem(
  checklistItem: TemplateDraftChecklistItem
): FormChecklistItem {
  return {
    _id: checklistItem._id,
    uniqueKeyId: checklistItem._id || checklistItem.uniqueKeyId,
    summary: checklistItem.summary,
    description: checklistItem.description,
    parentSectionId: checklistItem.parentSectionId || null,
    order: checklistItem.order || '',
    answers: (checklistItem.answers || []).map(toFormChecklistAnswer),
    errors: {
      summary: undefined,
      description: undefined,
      answers: [],
    },
    graphicUploader: undefined,
  };
}

function toFormSection(section: TemplateDraftSection): FormSection {
  return {
    _id: section._id,
    uniqueKeyId: section._id || section.uniqueKeyId,
    title: section.title,
    description: section.description,
    parentSectionId: section.parentSectionId || null,
    order: section.order || '',
    errors: {
      title: undefined,
      description: undefined,
    },
    graphicUploader: undefined,
  };
}

function toFormTemplateDefaultValues(
  defaultValues: TemplateDraftDefaultValues
): TemplateDefaultValues {
  return {
    _id: defaultValues._id,
    summary: defaultValues.summary,
    generalNorm: defaultValues.generalNorm,
    generalNormConfig: defaultValues.generalNormConfig,
    process: defaultValues.process,
    checklist: defaultValues.checklist.map(toFormChecklistItem),
    sections: defaultValues.sections.map(toFormSection),
  };
}

export function emptyDefaultValues(): TemplateDefaultValues {
  return toFormTemplateDefaultValues(createEmptyTemplateDraftDefaults());
}

export function toDefaultValues(
  template: InspectionTemplateModel
): TemplateDefaultValues {
  return toFormTemplateDefaultValues(toTemplateDraftDefaults(template));
}

export function permissionsRedirectWithMessage(
  redirect: () => void,
  message: string
): void {
  showErrorToastWithMessage(message);
  redirect();
}
